import { useState } from "react";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { AlertCircle, Copy } from "lucide-react";

import { createAxiosInstance } from "api/axiosConfig";

import style from "./ReferralPreview.module.css";

const formatBoolean = (field) => {
  if (field === "false") return "No";

  if (field === "true") return "Yes";

  return field;
};

export const capitalizeFirstLetter = (value) => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const formatDateOfBirth = (dateString) => {
  if (!dateString) return dateString;

  const ddMMYYYYPattern = /^\d{2}-\d{2}-\d{4}$/;
  if (ddMMYYYYPattern.test(dateString)) {
    return dateString;
  }

  const YYYYmmddPattern = /^\d{8}$/;
  if (YYYYmmddPattern.test(dateString)) {
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    return `${day}-${month}-${year}`;
  }

  return dateString;
};
export const ReferralPreview = ({
  data,
  isProstateReferral,
  isLungReferral,
  onRefresh,
  hideData,
}) => {
  const lungsAdditionalInfo = {
    informationForContrastCt: data.informationForContrastCt,
    otherClinicalFindings: data.otherClinicalFindings,
    otherRadInvestigations: data.otherRadInvestigations,
    hasPatientBeenAdvisedOfPossibleDiagnosisOfLungCancer:
      data.hasPatientBeenAdvisedOfPossibleDiagnosisOfLungCancer,
    otherPersistentUnexplainedSymptoms: data.otherPersistentUnexplainedSymptoms,
    haemoptysis: data.haemoptysis,
    clubbing: data.clubbing,
    lymphadenopathy: data.lymphadenopathy,
    hepatosplenomegaly: data.hepatosplenomegaly,
    chestSigns: data.chestSigns,
    historyOfAsthma: data.historyOfAsthma,
    historyOfRentalInsufficiency: data.historyOfRentalInsufficiency,
    historyOfAllergyToContrastDye: data.historyOfAllergyToContrastDye,
    lungLabInvestigation: data.lungLabInvestigation,
    specifiedChestSigns: data.specifiedChestSigns,
    radInvestigation1: data.radInvestigation1,
    radInvestigation1Hospital: data.radInvestigation1Hospital,
    radInvestigation1Date: data.radInvestigation1Date,
    radInvestigation1Result: data.radInvestigation1Result,
    radInvestigation1Comment: data.radInvestigation1Comment,
    radInvestigation2: data.radInvestigation2,
    radInvestigation2Hospital: data.radInvestigation2Hospital,
    radInvestigation2Date: data.radInvestigation2Date,
    radInvestigation2Result: data.radInvestigation2Result,
    radInvestigation2Comment: data.radInvestigation2Comment,
  };

  const prostateAdditionalInfo = {
    psaTests: data.psaTests,
    hasSeenUrologist: data.hasSeenUrologist,
    hasSeenUrologistConsultant: data.hasSeenUrologistConsultant,
    hasSeenUrologistLocations: data.hasSeenUrologistLocations,
    dateOfUrologyConsult: data.dateOfUrologyConsult,
    hasPreviousBiopsy: data.hasPreviousBiopsy,
    hasPreviousBiopsyHospital: data.hasPreviousBiopsyHospital,
    hasPreviousBiopsyDate: data.hasPreviousBiopsyDate,
    hasPreviousBiopsyResult: data.hasPreviousBiopsyResult,
    dateOfBiopsy: data.dateOfBiopsy,
    digitalRectalExam: data.digitalRectalExam,
    otherClinicalExam: data.otherClinicalExam,
    resultOfUrinalysis: data.resultOfUrinalysis,
    otherLabInvestigations: data.otherLabInvestigations,
    otherRadInvestigations: data.otherRadInvestigations,
  };

  const [isChecking, setIsChecking] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [copyStatus, setCopyStatus] = useState("Copy Link");

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const handleCopy = async () => {
    if (!data.xmlResponseData) return;
    try {
      await navigator.clipboard.writeText(data.xmlResponseData);
      setCopyStatus("Copied!");
      setTimeout(() => setCopyStatus("Copy Link"), 3000);
    } catch (err) {
      console.error("Failed to copy", err);
    }
  };

  const handleCheckStatus = async () => {
    setIsChecking(true);
    try {
      const axios = createAxiosInstance();
      const response = await axios.post(
        `${tenantVal}/healthlink/referrals/${data.id}/check-status`
      );

      if (response.data.confirmed) {
        toast.success("Referral confirmed as SENT.");
        if (onRefresh) onRefresh();
      } else {
        toast.error(
          "Healthlink has not confirmed receipt yet. Please ensure you have submitted the form in the new tab."
        );
      }
    } catch (error) {
      console.error("Error checking status:", error);
      toast.error("Could not verify status. Please try again later.");
    } finally {
      setIsChecking(false);
    }
  };

  const isIncompleteReferral = data.status === "DRAFT";

  return (
    <>
      {isIncompleteReferral && (
        <div
          className={style.itemWrapper}
          style={{
            border: "1px solid #fbbf24",
            backgroundColor: "#fffbeb",
            padding: "24px",
            borderRadius: "8px",
            marginBottom: "24px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        >
          <div
            style={{
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <AlertCircle size={20} color="#d97706" />
            <h4
              style={{
                margin: 0,
                color: "#92400e",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              Action Required: Complete Submission
            </h4>
          </div>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "16px",
                borderRadius: "6px",
                border: "1px solid #d9d8df",
              }}
            >
              <p
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                STEP 1: Copy & Paste Link
              </p>
              <div style={{ display: "flex", gap: "10px" }}>
                <input
                  readOnly
                  value={data.xmlResponseData}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    fontSize: "13px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    color: "#6b7280",
                    backgroundColor: "#f9fafb",
                  }}
                />
                <button
                  onClick={handleCopy}
                  style={{
                    whiteSpace: "nowrap",
                    backgroundColor: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#374151",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <Copy size={14} /> {copyStatus}
                </button>
              </div>
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginTop: "6px",
                  marginBottom: 0,
                }}
              >
                Paste this link into a new browser tab to access the secure
                form.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={isConfirmed}
                  onChange={(e) => setIsConfirmed(e.target.checked)}
                  style={{
                    width: "18px",
                    height: "18px",
                    accentColor: "#006546",
                  }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    color: "#1f2937",
                    fontWeight: "500",
                  }}
                >
                  I have submitted the form on Healthlink
                </span>
              </label>

              <button
                onClick={handleCheckStatus}
                disabled={!isConfirmed || isChecking}
                style={{
                  backgroundColor: isConfirmed ? "#006546" : "#d9d8df",
                  color: isConfirmed ? "white" : "#9ca3af",
                  border: "none",
                  padding: "10px 24px",
                  borderRadius: "6px",
                  cursor: isConfirmed ? "pointer" : "not-allowed",
                  fontWeight: "600",
                  fontSize: "14px",
                  transition: "all 0.2s",
                  minWidth: "140px",
                }}
              >
                {isChecking ? "Checking..." : "Check Status"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!hideData && (
        <>
          <div className={style.itemWrapper}>
            <div
              className={style.inputGroupGrid5Columns}
              style={{ marginTop: "24px" }}
            >
              <div>
                <p className={style.referralFormLabel}>Patient Name</p>
                <p className={style.referralFormValue}>
                  {data.firstName} {data.familyName}
                </p>
              </div>
              <div>
                <p className={style.referralFormLabel}>Gender</p>
                <p className={style.referralFormValue}>
                  {data.gender
                    ? data.gender.charAt(0).toUpperCase() +
                      data.gender.slice(1).toLowerCase()
                    : ""}
                </p>
              </div>
              <div>
                <p className={style.referralFormLabel}>Date of Birth</p>
                <p className={style.referralFormValue}>
                  {formatDateOfBirth(data.dateOfBirth)}
                </p>
              </div>
              <div>
                <p className={style.referralFormLabel}>Hospital</p>
                <p className={style.referralFormValue}>
                  {data.hospitalName || data.hospital || "_"}
                </p>
              </div>
              <div>
                <p className={style.referralFormLabel}>Referral Type</p>
                <p className={style.referralFormValue}>
                  {data.referralType
                    ? data.referralType.charAt(0).toUpperCase() +
                      data.referralType.slice(1).toLowerCase()
                    : ""}
                </p>
              </div>
            </div>
            <div className={style.inputGroupGrid5Columns}></div>
          </div>

          {!isLungReferral && (
            <div className={style.itemWrapper}>
              <div className={style.inputGroupGrid3Columns}>
                <div>
                  <p className={style.referralFormLabel}>Referral Priority</p>
                  <p className={style.referralFormValue}>
                    {capitalizeFirstLetter(
                      data.referralPriority?.toLowerCase()
                    ) || "-"}
                  </p>
                </div>
                {!isProstateReferral && (
                  <>
                    <div>
                      <p className={style.referralFormLabel}>
                        Preferred Consultant Family Name
                      </p>
                      <p className={style.referralFormValue}>
                        {data.preferredConsultantFamilyName || "-"}
                      </p>
                    </div>
                    <div>
                      <p className={style.referralFormLabel}>
                        Preferred Consultant First Name
                      </p>
                      <p className={style.referralFormValue}>
                        {data.preferredConsultantFirstName || "-"}
                      </p>
                    </div>
                  </>
                )}
              </div>
              {!isProstateReferral && (
                <div>
                  <p
                    className={style.referralFormLabel}
                    style={{ marginTop: "8px" }}
                  >
                    Reason for Referral / Anticipated Outcome
                  </p>
                  <p className={style.referralFormValue}>
                    {data.reasonForReferral || "-"}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className={style.itemWrapper}>
            <div className={style.inputGroupGrid3Columns}>
              <div>
                <p className={style.referralFormLabel}>
                  History of Presenting Complaints
                </p>
                <p className={style.referralFormValue}>
                  {data.historyOfPresentingComplains || "-"}
                </p>
              </div>
              {!isProstateReferral && (
                <div>
                  <p className={style.referralFormLabel}>
                    Previous Hospital Attendance
                  </p>
                  <p className={style.referralFormValue}>
                    {formatBoolean(data.previousHospitalAttendance) || "-"}
                  </p>
                </div>
              )}
              <div>
                <p className={style.referralFormLabel}>
                  History of Past Illness
                </p>
                <p className={style.referralFormValue}>
                  {data.historyOfPastIllness || "-"}
                </p>
              </div>
              <div>
                <p className={style.referralFormLabel}>
                  History of Surgical Procedures
                </p>
                <p className={style.referralFormValue}>
                  {data.historyOfSurgicalProcedures || "-"}
                </p>
              </div>
              <div>
                <p className={style.referralFormLabel}>
                  Allergies / Adverse Medication Events
                </p>
                <p className={style.referralFormValue}>
                  {data.allergiesAdverseMedicationEvents || "-"}
                </p>
              </div>
              <div>
                <p className={style.referralFormLabel}>
                  Relevant Family History
                </p>
                <p className={style.referralFormValue}>
                  {data.relevantFamilyHistory || "-"}
                </p>
              </div>
            </div>
          </div>

          {!isLungReferral && !isProstateReferral && (
            <>
              <div className={style.itemWrapper}>
                <div className={style.inputGroupGrid5Columns}>
                  <div>
                    <p className={style.referralFormLabel}>Pulse</p>
                    <p className={style.referralFormValue}>
                      {data.pulse ? `${data.pulse} bpm` : "-"}
                    </p>
                  </div>
                  <div>
                    <p className={style.referralFormLabel}>
                      BP Systolic/Diastolic
                    </p>
                    <p className={style.referralFormValue}>
                      {data.bp1 && data.bp2 ? `${data.bp1}/${data.bp2}` : "-"}
                    </p>
                  </div>
                  <div>
                    <p className={style.referralFormLabel}>Body Height</p>
                    <p className={style.referralFormValue}>
                      {data.bodyHeight ? `${data.bodyHeight}m` : "-"}
                    </p>
                  </div>
                  <div>
                    <p className={style.referralFormLabel}>Weight</p>
                    <p className={style.referralFormValue}>
                      {data.weight ? `${data.weight}kg` : "-"}
                    </p>
                  </div>
                  <div>
                    <p className={style.referralFormLabel}>Body Mass Index</p>
                    <p className={style.referralFormValue}>
                      {data.bmi ? `${data.bmi} kg/m2` : "-"}
                    </p>
                  </div>
                </div>
                {data.clinicalExamination && (
                  <div style={{ marginTop: "12px" }}>
                    <p className={style.referralFormLabel}>
                      Clinical Examination
                    </p>
                    <p className={style.referralFormValue}>
                      {data.clinicalExamination
                        ? `${data.clinicalExamination}`
                        : "-"}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {!isProstateReferral && !isLungReferral && (
            <div className={style.itemWrapper}>
              <div className={style.inputGroupGrid2Columns}>
                <div>
                  <p className={style.referralFormLabel}>
                    Laboratory Investigations
                  </p>

                  {data.laboratoryInvestigation?.length > 0
                    ? data.laboratoryInvestigation.map(
                        ({ title, date, result, unit }) => (
                          <p
                            className={style.referralFormValue}
                          >{`${title}: ${result}${
                            unit ? ` ${unit}` : ""
                          } on ${date}`}</p>
                        )
                      )
                    : "-"}
                </div>
                <div>
                  <p className={style.referralFormLabel}>
                    Radiology Investigations
                  </p>
                  {data.radiologyInvestigation?.length > 0
                    ? data.radiologyInvestigation.map(
                        ({ title, date, result, hospital }) => (
                          <p
                            className={style.referralFormValue}
                          >{`${title}: ${result}${
                            hospital ? ` in ${hospital}` : ""
                          } on ${date}`}</p>
                        )
                      )
                    : "-"}
                </div>
              </div>
            </div>
          )}

          <div className={style.itemWrapper}>
            <div className={style.inputGroupGrid2Columns}>
              <div className={style.inputGroupGrid2Columns}>
                <div>
                  <div>
                    <p className={style.referralFormLabel}>Drinker</p>
                    <p className={style.referralFormValue}>
                      {formatBoolean(data.drinker) || "-"}
                    </p>
                  </div>
                  {data?.drinker === "true" && data?.unitsOfAlcohol && (
                    <div style={{ marginTop: "8px" }}>
                      <p className={style.referralFormLabel}>
                        Units of Alcohol per week
                      </p>
                      <p className={style.referralFormValue}>
                        {data.unitsOfAlcohol}
                      </p>
                    </div>
                  )}
                </div>
                <div>
                  <div>
                    <p className={style.referralFormLabel}>Smoker</p>
                    <p className={style.referralFormValue}>
                      {capitalizeFirstLetter(
                        data.smoker?.toLowerCase()?.replace("_", " ")
                      ) || "-"}
                    </p>
                  </div>
                  {(data.smoker === "EX_SMOKER" ||
                    data.smoker === "CURRENT_SMOKER") && (
                    <div
                      className={style.inputGroupGrid2Columns}
                      style={{ marginTop: "8px" }}
                    >
                      <div>
                        <p className={style.referralFormLabel}>
                          Cigarettes per day
                        </p>
                        <p className={style.referralFormValue}>
                          {data.numberOfCigarettes}
                        </p>
                      </div>
                      <div>
                        <p className={style.referralFormLabel}>Years Smoking</p>
                        <p className={style.referralFormValue}>
                          {data.yearsSmoking}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <p className={style.referralFormLabel}>
                  Next of Kin (name, contact no. & relationship)
                </p>
                <p className={style.referralFormValue}>
                  {data.nextOfKin || "-"}
                </p>
              </div>
            </div>
          </div>

          <div className={style.itemWrapper}>
            {isProstateReferral ? (
              <div>
                <p className={style.referralFormLabel}>Current Medication</p>
                <div style={{ marginTop: "8px" }}>
                  <p className={style.referralFormLabel}>
                    Anticoagulant Use:{" "}
                    <span className={style.referralFormValue}>
                      {formatBoolean(data.patientOnAnticoagulants) || "-"}
                    </span>
                  </p>
                  {data.currentMedication && (
                    <div style={{ marginTop: "8px" }}>
                      <p className={style.referralFormValue}>
                        {data.currentMedication}
                      </p>
                    </div>
                  )}
                  {data.patientOnAnticoagulants === "true" && (
                    <div style={{ marginTop: "8px" }}>
                      {data.patientOnAspirin === "true" && (
                        <p className={style.referralFormValue}>
                          Aspirin
                          {data.aspirinDetails
                            ? `: ${data.aspirinDetails}`
                            : ""}
                        </p>
                      )}
                      {data.patientOnPlavix === "true" && (
                        <p className={style.referralFormValue}>
                          Plavix
                          {data.plavixDetails ? `: ${data.plavixDetails}` : ""}
                        </p>
                      )}
                      {data.patientOnWarfarin === "true" && (
                        <p className={style.referralFormValue}>
                          Warfarin
                          {data.warfarinDetails
                            ? `: ${data.warfarinDetails}`
                            : ""}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className={style.inputGroupGrid2Columns}>
                <div>
                  <p className={style.referralFormLabel}>Current Medication</p>
                  <p className={style.referralFormValue}>
                    {data.currentMedication || "-"}
                  </p>
                </div>
                <div>
                  <div>
                    <p className={style.referralFormLabel}>
                      Patient on Anticoagulants
                    </p>
                    <p className={style.referralFormValue}>
                      {formatBoolean(data.patientOnAnticoagulants) || "-"}
                    </p>
                  </div>
                  {data.patientOnAnticoagulants === "true" &&
                    data?.anticoagulantMedications?.length > 0 && (
                      <div style={{ marginTop: "8px" }}>
                        <p className={style.referralFormLabel}>
                          Anticoagulant Medications:
                        </p>

                        {isLungReferral && (
                          <>
                            <p
                              className={style.referralFormValue}
                              style={{ marginTop: "8px" }}
                            >
                              Patient on Aspirin:{" "}
                              {formatBoolean(data.patientOnAspirin) || "-"}
                              {data.aspirinDetails &&
                                ` - Details: ${data.aspirinDetails}`}
                            </p>
                            <p
                              className={style.referralFormValue}
                              style={{ marginTop: "8px" }}
                            >
                              Patient on Plavix:{" "}
                              {formatBoolean(data.patientOnPlavix) || "-"}
                              {data.plavixDetails &&
                                ` - Details: ${data.plavixDetails}`}
                            </p>
                            <p
                              className={style.referralFormValue}
                              style={{ marginTop: "8px" }}
                            >
                              Patient on Warfarin:{" "}
                              {formatBoolean(data.patientOnWarfarin) || "-"}
                              {data.warfarinDetails &&
                                ` - Details: ${data.warfarinDetails}`}
                            </p>
                          </>
                        )}

                        {!isLungReferral &&
                          !isProstateReferral &&
                          data.anticoagulantMedications.map((medication) => (
                            <p
                              className={style.referralFormValue}
                              style={{ marginTop: "8px" }}
                            >
                              Medication Name: {medication.name}, Dosage &
                              Frequency: {medication.dosage}
                            </p>
                          ))}
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>

          <div className={style.itemWrapper}>
            <div className={style.inputGroupGrid2Columns}>
              <div>
                <p className={style.referralFormLabel}>
                  Additional Relevant Information
                </p>
                <p className={style.referralFormValue}>
                  {data.additionalInformation || "-"}
                </p>
              </div>
            </div>
          </div>

          <div
            className={style.itemWrapper}
            style={{
              marginBottom: "0",
              paddingBottom: "0",
              borderBottom: "none",
            }}
          >
            {isLungReferral && (
              <>
                <div className={style.itemWrapper}>
                  <div
                    className={style.inputGroupGrid3Columns}
                    style={{ marginTop: "16px" }}
                  >
                    <div>
                      <p className={style.referralFormLabel}>
                        Symptom / Reason for Referral
                      </p>
                      <div style={{ marginTop: "8px" }}>
                        {lungsAdditionalInfo.haemoptysis && (
                          <p className={style.referralFormValue}>Haemoptysis</p>
                        )}
                        {!lungsAdditionalInfo.haemoptysis && (
                          <p className={style.referralFormValue}>-</p>
                        )}
                      </div>
                    </div>
                    {lungsAdditionalInfo.otherPersistentUnexplainedSymptoms && (
                      <div>
                        <p className={style.referralFormLabel}>
                          Other Persistent Unexplained Symptoms:
                        </p>
                        <p className={style.referralFormValue}>
                          {
                            lungsAdditionalInfo.otherPersistentUnexplainedSymptoms
                          }
                        </p>
                      </div>
                    )}
                    {lungsAdditionalInfo.otherClinicalFindings && (
                      <div>
                        <p className={style.referralFormLabel}>
                          Other Clinical Findings
                        </p>
                        <p className={style.referralFormValue}>
                          {lungsAdditionalInfo.otherClinicalFindings}
                        </p>
                      </div>
                    )}
                  </div>
                  <div
                    className={style.inputGroupGrid5Columns}
                    style={{ marginTop: "16px" }}
                  >
                    <div>
                      <div>
                        <p className={style.referralFormLabel}>Chest Signs</p>
                        <p className={style.referralFormValue}>
                          {lungsAdditionalInfo.chestSigns ? "Yes" : "No"}
                        </p>
                      </div>
                    </div>
                    {lungsAdditionalInfo.chestSigns &&
                      lungsAdditionalInfo.specifiedChestSigns && (
                        <div>
                          <p className={style.referralFormLabel}>
                            Specified Chest Signs
                          </p>
                          <p className={style.referralFormValue}>
                            {lungsAdditionalInfo.specifiedChestSigns}
                          </p>
                        </div>
                      )}
                    <div>
                      <p className={style.referralFormLabel}>Lymphadenopathy</p>
                      <p className={style.referralFormValue}>
                        {lungsAdditionalInfo.lymphadenopathy ? "Yes" : "No"}
                      </p>
                    </div>
                    <div>
                      <p className={style.referralFormLabel}>Hepatomegaly</p>
                      <p className={style.referralFormValue}>
                        {lungsAdditionalInfo.hepatomegaly ? "Yes" : "No"}
                      </p>
                    </div>
                    <div>
                      <div>
                        <p className={style.referralFormLabel}>Clubbing</p>
                        <p className={style.referralFormValue}>
                          {lungsAdditionalInfo.clubbing ? "Yes" : "No"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {lungsAdditionalInfo.lungLabInvestigation && (
                  <div className={style.itemWrapper}>
                    <div
                      className={style.inputGroupGrid2Columns}
                      style={{ marginTop: "16px" }}
                    >
                      <div>
                        <p className={style.referralFormLabel}>
                          Lab Investigation
                        </p>
                        <p className={style.referralFormValue}>
                          {lungsAdditionalInfo.lungLabInvestigation}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className={style.itemWrapper}>
                  <div
                    className={style.inputGroupGrid2Columns}
                    style={{ marginTop: "16px" }}
                  >
                    <div>
                      <p className={style.referralFormLabel}>
                        Rad Investigations
                      </p>
                      {lungsAdditionalInfo?.radInvestigation1 === "not-done" &&
                        lungsAdditionalInfo?.radInvestigation2 ===
                          "not-done" && <p>Not done</p>}
                      {lungsAdditionalInfo.radInvestigation1 &&
                        lungsAdditionalInfo.radInvestigation1 !==
                          "not-done" && (
                          <p
                            className={style.referralFormValue}
                            style={{ marginTop: "8px" }}
                          >
                            {lungsAdditionalInfo.radInvestigation1} on{" "}
                            {lungsAdditionalInfo.radInvestigation1Date} by{" "}
                            {lungsAdditionalInfo.radInvestigation1Hospital}
                            {lungsAdditionalInfo.radInvestigation1Result ===
                              "Abnormal" &&
                              !!lungsAdditionalInfo.radInvestigation1Comment && (
                                <>
                                  {" "}
                                  — {lungsAdditionalInfo.radInvestigation1Comment}
                                </>
                              )}
                          </p>
                        )}

                      {lungsAdditionalInfo.radInvestigation2 &&
                        lungsAdditionalInfo.radInvestigation2 !==
                          "not-done" && (
                          <p
                            className={style.referralFormValue}
                            style={{ marginTop: "8px" }}
                          >
                            {lungsAdditionalInfo.radInvestigation2} on{" "}
                            {lungsAdditionalInfo.radInvestigation2Date} by{" "}
                            {lungsAdditionalInfo.radInvestigation2Hospital}
                            {lungsAdditionalInfo.radInvestigation2Result ===
                              "Abnormal" &&
                              !!lungsAdditionalInfo.radInvestigation2Comment && (
                                <>
                                  {" "}
                                  — {lungsAdditionalInfo.radInvestigation2Comment}
                                </>
                              )}
                          </p>
                        )}
                    </div>
                    <div>
                      <p className={style.referralFormLabel}>
                        Other Rad Investigations
                      </p>
                      <p className={style.referralFormValue}>
                        {lungsAdditionalInfo.otherRadInvestigations}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  className={style.itemWrapper}
                  style={{
                    marginBottom: "0",
                    paddingBottom: "0",
                    borderBottom: "none",
                  }}
                >
                  <div
                    className={style.inputGroupGrid4Columns}
                    style={{ marginTop: "16px" }}
                  >
                    <div>
                      <p className={style.referralFormLabel}>
                        History of Asthma{" "}
                      </p>
                      <p className={style.referralFormValue}>
                        {lungsAdditionalInfo.historyOfAsthma ? "Yes" : "No"}
                      </p>
                    </div>
                    <div>
                      <p className={style.referralFormLabel}>
                        History of Rental Insufficiency
                      </p>
                      <p className={style.referralFormValue}>
                        {lungsAdditionalInfo.historyOfRentalInsufficiency
                          ? "Yes"
                          : "No"}
                      </p>
                    </div>
                    <div>
                      <p className={style.referralFormLabel}>
                        History of Allergy to Contrast Dye
                      </p>
                      <p className={style.referralFormValue}>
                        {lungsAdditionalInfo.historyOfAllergyToContrastDye
                          ? "Yes"
                          : "No"}
                      </p>
                    </div>
                    <div>
                      <p className={style.referralFormLabel}>
                        Has patient been advised of possible diagnosis of lung
                        cancer?
                      </p>
                      <p className={style.referralFormValue}>
                        {lungsAdditionalInfo.hasPatientBeenAdvisedOfPossibleDiagnosisOfLungCancer
                          ? "Yes"
                          : "No"}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
            {isProstateReferral && (
              <>
                <div className={style.itemWrapper}>
                  <div className={style.inputGroupGrid4Columns}>
                    <div>
                      <p className={style.referralFormLabel}>
                        Previous Seen by Urologist
                      </p>
                      <p className={style.referralFormValue}>
                        {data.hasSeenUrologist
                          ? data.hasSeenUrologist.charAt(0).toUpperCase() +
                            data.hasSeenUrologist.slice(1).toLowerCase()
                          : "-"}
                      </p>
                      {data.hasSeenUrologist === "yes" &&
                        prostateAdditionalInfo.hasSeenUrologistConsultant && (
                          <p className={style.referralFormValue}>
                            Consultant:{" "}
                            {prostateAdditionalInfo.hasSeenUrologistConsultant}
                          </p>
                        )}
                      {data.hasSeenUrologist === "yes" &&
                        prostateAdditionalInfo.hasSeenUrologistLocations && (
                          <p className={style.referralFormValue}>
                            Location:{" "}
                            {prostateAdditionalInfo.hasSeenUrologistLocations}
                          </p>
                        )}
                    </div>
                    {data.hasSeenUrologist === "yes" && (
                      <div>
                        <p className={style.referralFormLabel}>
                          Date Seen by Urologist
                        </p>
                        <p className={style.referralFormValue}>
                          {data.dateOfUrologyConsult || "-"}
                        </p>
                      </div>
                    )}

                    <div>
                      <p className={style.referralFormLabel}>
                        Digital Rectal Exam
                      </p>
                      <p className={style.referralFormValue}>
                        {data.digitalRectalExam || "-"}
                      </p>
                    </div>
                    <div>
                      <p className={style.referralFormLabel}>
                        Other Clinical Exam
                      </p>
                      <p className={style.referralFormValue}>
                        {data.otherClinicalExam || "-"}
                      </p>
                    </div>
                  </div>

                  {data.psaTests && data.psaTests.length > 0 && (
                    <div style={{ marginTop: "16px" }}>
                      <p
                        className={style.referralFormLabel}
                        style={{ marginBottom: "8px" }}
                      >
                        PSA Tests
                      </p>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        {data.psaTests.map((test, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              gap: "16px",
                            }}
                          >
                            <div>
                              <span className={style.referralFormValue}>
                                {test.psa} ng/ml
                              </span>
                            </div>
                            <div>
                              <span className={style.referralFormValue}>
                                {test.date}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div
                    className={style.inputGroupGrid3Columns}
                    style={{ marginTop: "16px" }}
                  >
                    <div>
                      <p className={style.referralFormLabel}>
                        Result of Urinalysis
                      </p>
                      <p className={style.referralFormValue}>
                        {data.resultOfUrinalysis || "-"}
                      </p>
                    </div>
                    <div>
                      <p className={style.referralFormLabel}>
                        Other Lab Investigations
                      </p>
                      <p className={style.referralFormValue}>
                        {data.otherLabInvestigations || "-"}
                      </p>
                    </div>
                    <div>
                      <p className={style.referralFormLabel}>
                        Rad Investigations
                      </p>
                      <p className={style.referralFormValue}>
                        {prostateAdditionalInfo.otherRadInvestigations || "-"}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  className={style.itemWrapper}
                  style={{
                    marginBottom: "0",
                    paddingBottom: "0",
                    borderBottom: "none",
                  }}
                >
                  <div
                    className={style.inputGroupGrid4Columns}
                    style={{ marginTop: "16px" }}
                  >
                    <div>
                      <p className={style.referralFormLabel}>
                        Previous Prostate Biopsy
                      </p>
                      <p className={style.referralFormValue}>
                        {prostateAdditionalInfo.hasPreviousBiopsy === "yes"
                          ? "Yes"
                          : "No"}
                      </p>
                    </div>
                    {prostateAdditionalInfo.hasPreviousBiopsy === "yes" && (
                      <div>
                        <p className={style.referralFormLabel}>
                          Previous Biopsy Hospital
                        </p>
                        <p className={style.referralFormValue}>
                          {prostateAdditionalInfo.hasPreviousBiopsyHospital ||
                            "-"}
                        </p>
                      </div>
                    )}
                    {prostateAdditionalInfo.hasPreviousBiopsy === "yes" && (
                      <div>
                        <p className={style.referralFormLabel}>
                          Date of Previous Biopsy
                        </p>
                        <p className={style.referralFormValue}>
                          {prostateAdditionalInfo.hasPreviousBiopsyDate || "-"}
                        </p>
                      </div>
                    )}
                    {prostateAdditionalInfo.hasPreviousBiopsy === "yes" && (
                      <div>
                        <p className={style.referralFormLabel}>
                          Previous Biopsy Result
                        </p>
                        <p className={style.referralFormValue}>
                          {prostateAdditionalInfo.hasPreviousBiopsyResult ||
                            "-"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};
